import { useState } from 'react';

import { Link } from 'react-router-dom';

import { ApiError } from '@/api/client';
import type { CandidateRead, DuplicateMatch } from '@/api/schema';
import { Alert } from '@/components/feedback/Alert';
import { useToast } from '@/components/feedback/Toast';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Combobox } from '@/components/ui/Combobox';
import { EditorColumns, EditorPage } from '@/components/layout/EditorPage';
import { Panel } from '@/components/layout/Page';
import { useUnsavedChanges } from '@/lib/useUnsavedChanges';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useUsers } from '@/features/users/api';
import { formatDate, todayIso, type DateFormat } from '@/lib/dates';
import { NOTHING } from '@/lib/nothing';

import {
  useAttachCandidateFile,
  useCandidateDepartments,
  useCountries,
  useCreateCandidate,
  usePositions,
  type AttachmentKind,
} from '../api';
import {
  APPLYING_FOR_OPTIONS,
  DOCUMENTATION_OPTIONS,
  PAYMENT_OPTIONS,
  POINTS_OPTIONS,
  SOURCE_OPTIONS,
  STATUS_OPTIONS,
  referenceOptions,
} from '../labels';
import {
  candidateFieldsSchema,
  changeCandidateField,
  emptyFields,
  messagesFrom,
  serverMessageFor,
  toCreateBody,
  type CandidateFieldsDraft,
  type FieldMessages,
} from '../schema';
import {
  AttachmentField,
  EMPTY_ATTACHMENT,
  documentProblem,
  type AttachmentState,
} from './AttachmentField';
import styles from './CandidateForm.module.css';

const FORM_ID = 'candidate-create';

type Saved = {
  candidate: CandidateRead;
  duplicates: DuplicateMatch[];
};

/**
 * Create a candidate.
 *
 * Two requests, in order: the record is created as JSON, and only then can a document
 * be attached to it; the upload route looks the candidate up before it will take a
 * file. That order is the whole reason this component has two states rather than one.
 *
 * Once the record exists it is never un-created. There is no rollback and nothing to
 * roll back to: both attachment columns are nullable, so a candidate with no CV is a
 * perfectly legal record. If an upload then fails, the drawer says exactly that
 * (the person was saved, the file was not) and offers to try the file again. Pretending
 * the whole thing failed would be a lie that costs somebody their typing.
 */
export function CandidateCreateForm({
  backTo = '/candidates',
  onClose,
  dateFormat,
}: {
  onClose: () => void;
  dateFormat: DateFormat;
  backTo?: string;
}) {
  const toast = useToast();
  const create = useCreateCandidate();
  const attach = useAttachCandidateFile();

  /** The form as it opened, kept to compare against for the discard question below. */
  const [pristine] = useState(() => emptyFields(todayIso()));
  const [draft, setDraft] = useState<CandidateFieldsDraft>(pristine);
  const [messages, setMessages] = useState<FieldMessages>({});
  const [cv, setCv] = useState<AttachmentState>(EMPTY_ATTACHMENT);
  const [passport, setPassport] = useState<AttachmentState>(EMPTY_ATTACHMENT);

  /** Set once the record exists. From then on this form never creates again. */
  const [saved, setSaved] = useState<Saved | null>(null);

  const departments = useCandidateDepartments();
  const positions = usePositions();
  const countries = useCountries();
  const staff = useUsers({
    page: 1,
    per_page: 100,
    search: '',
    status: 'active',
    department_id: '',
    sort_by: 'full_name',
    sort_dir: 'asc',
  });

  const error = create.error instanceof ApiError ? create.error : null;

  /**
   * Whether closing now would throw anything away.
   *
   * Compared by value and not by identity: `set` builds a whole new draft on every
   * keystroke, so a field typed into and then cleared again would count as work at
   * stake and put a question in front of somebody who has nothing to lose.
   */
  const dirty =
    cv.file !== null ||
    passport.file !== null ||
    (Object.keys(pristine) as (keyof CandidateFieldsDraft)[]).some(
      (key) => draft[key] !== pristine[key],
    );

  const set = <K extends keyof CandidateFieldsDraft>(
    key: K,
    value: CandidateFieldsDraft[K],
  ) => {
    setDraft((current) => changeCandidateField(current, key, value));
    setMessages((current) => ({ ...current, [key]: undefined }));
  };

  const messageFor = (field: keyof CandidateFieldsDraft) =>
    messages[field] ?? serverMessageFor(error, field);

  const pick = (
    setter: (next: AttachmentState) => void,
    label: string,
  ): ((file: File) => void) => {
    return (file) => {
      const problem = documentProblem(file);
      setter(
        problem
          ? { ...EMPTY_ATTACHMENT, error: `${label}: ${problem}` }
          : { file, progress: null, error: null, done: false },
      );
    };
  };

  /** Send whichever documents are still outstanding, one after the other. */
  const uploadOutstanding = async (candidateId: string) => {
    const slots: {
      kind: AttachmentKind;
      state: AttachmentState;
      setter: (next: AttachmentState) => void;
    }[] = [
      { kind: 'cv', state: cv, setter: setCv },
      { kind: 'passport', state: passport, setter: setPassport },
    ];

    let allDone = true;

    // Sequential, not parallel: two large files racing over one connection halve each
    // other's throughput and make both progress bars misleading.
    for (const slot of slots) {
      if (!slot.state.file || slot.state.done) continue;

      slot.setter({ ...slot.state, progress: 0, error: null });
      try {
        await attach(candidateId, slot.kind, slot.state.file, (fraction) => {
          slot.setter({ ...slot.state, progress: fraction, error: null });
        });
        slot.setter({ ...slot.state, progress: null, error: null, done: true });
      } catch (cause) {
        allDone = false;
        slot.setter({
          ...slot.state,
          progress: null,
          done: false,
          error: cause instanceof ApiError ? cause.message : 'That upload did not go through.',
        });
      }
    }

    return allDone;
  };

  const submit = async () => {
    // Idempotent by construction: once the record exists this branch is the only one
    // that runs, so no double click and no retry can produce a second candidate.
    if (saved) {
      const allDone = await uploadOutstanding(saved.candidate.id);
      if (allDone) {
        toast.success(`${saved.candidate.full_name} saved.`);
        unsaved.allowSavedExit();
        onClose();
      }
      return;
    }

    const parsed = candidateFieldsSchema.safeParse(draft);
    if (!parsed.success) {
      setMessages(messagesFrom(parsed.error));
      return;
    }

    let created;
    try {
      created = await create.mutateAsync(toCreateBody(parsed.data));
    } catch {
      // Nothing has been sent but JSON, so every field and both chosen files are
      // exactly where they were. The banner and the field messages say what to fix.
      return;
    }

    // The field has a server-side default, so the generated type marks it optional.
    const duplicates = created.possible_duplicates ?? [];
    setSaved({ candidate: created.candidate, duplicates });

    const allDone = await uploadOutstanding(created.candidate.id);
    if (allDone && duplicates.length === 0) {
      toast.success(`${created.candidate.full_name} added as ${created.candidate.reference}.`);
      unsaved.allowSavedExit();
      onClose();
    }
  };

  const busy = create.isPending || cv.progress !== null || passport.progress !== null;
  const unsaved = useUnsavedChanges((!saved && dirty) || busy);
  const stillToSend = (slot: AttachmentState) => slot.file !== null && !slot.done;
  const outstanding = stillToSend(cv) || stillToSend(passport);

  return (
    <EditorPage
      backTo={backTo}
      backLabel="Back to candidates"
      unsaved={unsaved}
      busy={busy}
      title="Add a candidate"
      description={
        saved ? saved.candidate.reference : 'They will start at the Introduction stage.'
      }
      actions={
        saved ? (
          <>
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              Done
            </Button>
            {outstanding ? (
              <Button loading={busy} onClick={() => void submit()}>
                Retry upload
              </Button>
            ) : null}
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" form={FORM_ID} loading={busy}>
              Add candidate
            </Button>
          </>
        )
      }
    >
      {/* Saved, but something else needs saying. The record is real either way. */}
      {saved ? (
        <div className={styles.settled}>
          {outstanding ? (
            <Alert tone="warning">
              <b>
                {saved.candidate.full_name} was created as {saved.candidate.reference}.
              </b>{' '}
              The document below did not attach. The record is saved either way. Try again, or
              open it and attach the file later.
            </Alert>
          ) : (
            <Alert tone="success">
              <b>
                {saved.candidate.full_name} was created as {saved.candidate.reference}.
              </b>
            </Alert>
          )}

          {saved.duplicates.length > 0 ? (
            <div className={styles.duplicates}>
              <p className={styles.duplicatesHead}>
                {saved.duplicates.length === 1
                  ? 'One existing record shares a contact detail with this one.'
                  : `${saved.duplicates.length} existing records share a contact detail with this one.`}{' '}
                Worth checking whether it is the same person. This one was still created.
              </p>
              <ul className={styles.duplicateList}>
                {saved.duplicates.map((match) => (
                  <li key={match.id}>
                    <Link to={`/candidates/${match.id}`} target="_blank" rel="noreferrer">
                      {match.full_name} · {match.reference}
                    </Link>
                    <span className={styles.duplicateMeta}>
                      {match.email ?? match.phone ?? NOTHING} · received{' '}
                      {formatDate(match.received_on, dateFormat)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <Link className={styles.openRecord} to={`/candidates/${saved.candidate.id}`}>
            Open the record
          </Link>
        </div>
      ) : null}

      {error && !error.isValidation && error.code !== 'UNKNOWN_REFERENCE' ? (
        <Alert tone="danger">{error.message}</Alert>
      ) : null}

      <form
        id={FORM_ID}
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {/* Locked once the record exists: this form only ever creates, so an edit made
            afterwards would go nowhere at all. */}
        <EditorColumns
          settings={
            <>
              <Panel>
                <fieldset className={styles.section} disabled={Boolean(saved)}>
                  <legend className={styles.legend}>Tracking</legend>

                  <div className={styles.pair}>
                    <Field label="Status">
                      {({ id }) => (
                        <Select
                          id={id}
                          value={draft.status}
                          options={STATUS_OPTIONS}
                          onChange={(event) => {
                            set('status', event.target.value);
                          }}
                        />
                      )}
                    </Field>
                    {draft.status === 'rsr_screening_arranged' ? (
                      <Field
                        label="RSR screening date"
                        required
                        hint="The candidate will appear on the RSR board for this booking."
                      >
                        {(ids) => (
                          <Input
                            {...ids}
                            type="date"
                            required
                            value={draft.rsr_booking_date}
                            onChange={(event) => {
                              set('rsr_booking_date', event.target.value);
                            }}
                          />
                        )}
                      </Field>
                    ) : null}

                    <Field label="Sponsor points" error={messageFor('sponsor_points')}>
                      {({ id, describedBy, invalid }) => (
                        <Select
                          id={id}
                          describedBy={describedBy}
                          invalid={invalid}
                          value={draft.sponsor_points}
                          options={POINTS_OPTIONS}
                          onChange={(event) => {
                            set('sponsor_points', event.target.value);
                          }}
                        />
                      )}
                    </Field>
                  </div>

                  <div className={styles.pair}>
                    <Field label="Documentation">
                      {({ id }) => (
                        <Select
                          id={id}
                          value={draft.documentation}
                          options={DOCUMENTATION_OPTIONS}
                          onChange={(event) => {
                            set('documentation', event.target.value);
                          }}
                        />
                      )}
                    </Field>
                    <Field label="Payment">
                      {({ id }) => (
                        <Select
                          id={id}
                          value={draft.payment_status}
                          options={PAYMENT_OPTIONS}
                          onChange={(event) => {
                            set('payment_status', event.target.value);
                          }}
                        />
                      )}
                    </Field>
                  </div>

                  <Field label="Assigned to" error={messageFor('assigned_to')}>
                    {({ id, describedBy, invalid }) => (
                      <Combobox
                        id={id}
                        describedBy={describedBy}
                        invalid={invalid}
                        placeholder="Unassigned"
                        value={draft.assigned_to}
                        options={[
                          { value: '', label: 'Unassigned' },
                          ...(staff.data?.items ?? []).map((user) => ({
                            value: user.id,
                            label: user.full_name,
                            meta: user.email,
                          })),
                        ]}
                        onChange={(value) => {
                          set('assigned_to', value);
                        }}
                      />
                    )}
                  </Field>

                  <div className={styles.checks}>
                    <Checkbox
                      label="Contacted"
                      checked={draft.contacted}
                      onChange={(event) => {
                        set('contacted', event.target.checked);
                      }}
                    />
                    <Checkbox
                      label="Details shared"
                      checked={draft.details_shared}
                      onChange={(event) => {
                        set('details_shared', event.target.checked);
                      }}
                    />
                    <Checkbox
                      label="Follow-up done"
                      checked={draft.followup_done}
                      onChange={(event) => {
                        set('followup_done', event.target.checked);
                      }}
                    />
                  </div>
                </fieldset>
              </Panel>

              <Panel>
                <fieldset className={styles.section}>
                  <legend className={styles.legend}>Documents</legend>
                  {/* Not disabled after saving: this is the half that may still need doing. */}
                  <p className={styles.sectionNote}>
                    Attached after the record is created, so these send once you add the
                    candidate.
                  </p>

                  <AttachmentField
                    label="CV"
                    hint="PDF, Word or an image, up to 10 MB."
                    state={cv}
                    onPick={pick(setCv, 'CV')}
                    onClear={() => {
                      setCv(EMPTY_ATTACHMENT);
                    }}
                    disabled={busy}
                  />
                  <AttachmentField
                    label="Passport"
                    hint="A scan or a clear photograph, up to 10 MB."
                    state={passport}
                    onPick={pick(setPassport, 'Passport')}
                    onClear={() => {
                      setPassport(EMPTY_ATTACHMENT);
                    }}
                    disabled={busy}
                  />
                </fieldset>
              </Panel>
            </>
          }
        >
          <Panel>
            <fieldset className={styles.section} disabled={Boolean(saved)}>
              <legend className={styles.legend}>Personal details</legend>

              <Field label="Full name" required error={messageFor('full_name')}>
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    describedBy={describedBy}
                    invalid={invalid}
                    value={draft.full_name}
                    onChange={(event) => {
                      set('full_name', event.target.value);
                    }}
                  />
                )}
              </Field>

              <div className={styles.pair}>
                <Field label="Email" error={messageFor('email')}>
                  {({ id, describedBy, invalid }) => (
                    <Input
                      id={id}
                      type="email"
                      describedBy={describedBy}
                      invalid={invalid}
                      value={draft.email}
                      onChange={(event) => {
                        set('email', event.target.value);
                      }}
                    />
                  )}
                </Field>
                <Field label="Phone" error={messageFor('phone')}>
                  {({ id, describedBy, invalid }) => (
                    <Input
                      id={id}
                      describedBy={describedBy}
                      invalid={invalid}
                      value={draft.phone}
                      onChange={(event) => {
                        set('phone', event.target.value);
                      }}
                    />
                  )}
                </Field>
              </div>

              <div className={styles.pair}>
                <Field label="City" error={messageFor('city')}>
                  {({ id, describedBy, invalid }) => (
                    <Input
                      id={id}
                      describedBy={describedBy}
                      invalid={invalid}
                      value={draft.city}
                      onChange={(event) => {
                        set('city', event.target.value);
                      }}
                    />
                  )}
                </Field>
                <Field label="Country" error={messageFor('country')}>
                  {({ id, describedBy, invalid }) => (
                    <Input
                      id={id}
                      describedBy={describedBy}
                      invalid={invalid}
                      value={draft.country}
                      onChange={(event) => {
                        set('country', event.target.value);
                      }}
                    />
                  )}
                </Field>
              </div>
            </fieldset>
          </Panel>

          <Panel>
            <fieldset className={styles.section} disabled={Boolean(saved)}>
              <legend className={styles.legend}>Application</legend>

              <div className={styles.pair}>
                <Field label="Received on" required error={messageFor('received_on')}>
                  {({ id, describedBy, invalid }) => (
                    <Input
                      id={id}
                      type="date"
                      describedBy={describedBy}
                      invalid={invalid}
                      value={draft.received_on}
                      onChange={(event) => {
                        set('received_on', event.target.value);
                      }}
                    />
                  )}
                </Field>
                <Field label="Source" required error={messageFor('source')}>
                  {({ id, describedBy, invalid }) => (
                    <Select
                      id={id}
                      describedBy={describedBy}
                      invalid={invalid}
                      placeholder="Where did it come from?"
                      value={draft.source}
                      options={SOURCE_OPTIONS}
                      onChange={(event) => {
                        set('source', event.target.value);
                      }}
                    />
                  )}
                </Field>
              </div>

              <div className={styles.pair}>
                <Field label="Applying for" required error={messageFor('applying_for')}>
                  {({ id, describedBy, invalid }) => (
                    <Select
                      id={id}
                      describedBy={describedBy}
                      invalid={invalid}
                      placeholder="Internship or placement?"
                      value={draft.applying_for}
                      options={APPLYING_FOR_OPTIONS}
                      onChange={(event) => {
                        set('applying_for', event.target.value);
                      }}
                    />
                  )}
                </Field>
                <Field label="Department" error={messageFor('department_id')}>
                  {({ id, describedBy, invalid }) => (
                    <Select
                      id={id}
                      describedBy={describedBy}
                      invalid={invalid}
                      placeholder="No department"
                      value={draft.department_id}
                      options={referenceOptions(departments.data, draft.department_id)}
                      onChange={(event) => {
                        set('department_id', event.target.value);
                      }}
                    />
                  )}
                </Field>
              </div>

              <div className={styles.pair}>
                <Field label="Preferred position" error={messageFor('preferred_position_id')}>
                  {({ id, describedBy, invalid }) => (
                    <Select
                      id={id}
                      describedBy={describedBy}
                      invalid={invalid}
                      placeholder="No preference"
                      value={draft.preferred_position_id}
                      options={referenceOptions(positions.data, draft.preferred_position_id)}
                      onChange={(event) => {
                        set('preferred_position_id', event.target.value);
                      }}
                    />
                  )}
                </Field>
                <Field label="Preferred country" error={messageFor('preferred_country_id')}>
                  {({ id, describedBy, invalid }) => (
                    <Select
                      id={id}
                      describedBy={describedBy}
                      invalid={invalid}
                      placeholder="No preference"
                      value={draft.preferred_country_id}
                      options={referenceOptions(countries.data, draft.preferred_country_id)}
                      onChange={(event) => {
                        set('preferred_country_id', event.target.value);
                      }}
                    />
                  )}
                </Field>
              </div>
            </fieldset>
          </Panel>
        </EditorColumns>
      </form>
    </EditorPage>
  );
}
